import { Mat4 } from 'playcanvas';

import { ElementType } from '../element';
import { Events } from '../events';
import { fixedTransform } from '../gaussian-bake';
import { DirectoryFileSystem } from '../io';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { ColorGradeCache, createGpuDevice, SplatTransformCache, WebGPUUnavailableError } from '../splat-serialize';
import { EditState } from './edit-state';
import { parseLcc2Index } from './lcc2-index';
import { exportLcc2 } from './lcc2-writer';
import { lcc2ImportMatrix } from './origin';
import { i18n } from '../ui/localization';

const SOG_ITERATIONS = 10;

// visible layers of one LCC2 resource, or null when the scene holds anything else
const lcc2Layers = (scene: Scene) => {
    const splats = (scene.getElementsByType(ElementType.splat) as Splat[]).filter(s => s.visible && s.numSplats > 0);
    const resource = splats[0]?.resource;
    if (!resource?.lcc2Origin || splats.some(s => s.resource !== resource)) return null;
    return { resource, splats };
};

// never write into an existing folder: pick "<base> bearbeitet", then " 2", " 3" …
const createUniqueFolder = async (parent: FileSystemDirectoryHandle, base: string) => {
    for (let n = 1; ; ++n) {
        const name = n === 1 ? `${base} bearbeitet` : `${base} bearbeitet ${n}`;
        try {
            await parent.getDirectoryHandle(name);
        } catch (err) {
            if (err.name !== 'NotFoundError') throw err;
            return { name, handle: await parent.getDirectoryHandle(name, { create: true }) };
        }
    }
};

const registerLcc2ExportHandler = (events: Events, scene: Scene) => {
    events.function('scene.exportLcc2', async () => {
        const layers = lcc2Layers(scene);
        if (!layers) {
            await events.invoke('showPopup', {
                type: 'error',
                header: i18n.t('popup.error'),
                message: i18n.t('lcc2.export.unavailable')
            });
            return;
        }
        const { resource, splats } = layers;
        const origin = resource.lcc2Origin;

        const parent: FileSystemDirectoryHandle | null = await events.invoke('scene.pickExportDirectory');
        if (!parent) return;

        let folder = '';
        events.fire('progressStart', i18n.t('lcc2.export.progress'));
        try {
            const index = parseLcc2Index(origin.indexText);
            const target = await createUniqueFolder(parent, origin.indexName.replace(/\.lcc2$/i, ''));
            folder = target.name;

            // editor space -> LCC2 space: undo the import rotation
            const toLcc2 = lcc2ImportMatrix().invert();
            const transforms = splats.map(s => new SplatTransformCache(s, false, toLcc2));
            const grades = splats.map(s => new ColorGradeCache(s));
            const state = EditState.build(index.json.lodSplats[origin.lod], splats.map(s => s.instances), origin.rowToStream);

            // the environment follows the layer holding most of the scene
            const main = splats.reduce((a, b) => (b.instances.count > a.instances.count ? b : a));
            const envMat = new Mat4().mul2(toLcc2, main.entity.getWorldTransform());

            await exportLcc2({
                index,
                indexName: origin.indexName,
                readFs: origin.fileSystem,
                writeFs: new DirectoryFileSystem(target.handle),
                edits: { loadedLevel: origin.lod, state, transforms, grades, envTransform: fixedTransform(envMat) },
                copyFiles: origin.files.filter(f => f.startsWith('info/')),
                iterations: SOG_ITERATIONS,
                createDevice: createGpuDevice,
                onProgress: async (done, total, label) => {
                    events.fire('progressUpdate', { text: label, progress: 100 * done / total });
                    // let the progress dialog paint between files
                    await new Promise<void>((resolve) => {
                        setTimeout(resolve);
                    });
                }
            });

            events.fire('progressEnd');
            await events.invoke('showPopup', {
                type: 'info',
                header: i18n.t('lcc2.export.progress'),
                message: i18n.t('lcc2.export.done', { folder })
            });
        } catch (error) {
            events.fire('progressEnd');
            console.error(error);
            const message = error instanceof WebGPUUnavailableError ?
                i18n.t('popup.webgpu-unavailable') :
                (error instanceof Error ? error.message : String(error));
            await events.invoke('showPopup', {
                type: 'error',
                header: i18n.t('popup.error'),
                message: i18n.t('lcc2.export.failed', { message, folder })
            });
        }
    });
};

export { registerLcc2ExportHandler };
